import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl, formatArea, formatPriceHuf } from "@/lib/format";
import { arePricesHidden, unitDisplayPriceHuf } from "@shared/pricing";
import { RequestQuoteLink, unitQuoteHref } from "@/components/request-quote";
import { useDocumentTitle } from "@/lib/use-document-title";
import { ScrollReveal } from "@/components/scroll-reveal";
import {
  UNIT_STATUS_BADGE,
  UNIT_STATUS_LABEL,
  unitSlug,
} from "@/lib/unit-helpers";
import type { Project, ProjectUnit } from "@shared/schema";

type Detail = { project: Project; units: ProjectUnit[] };

const MAX_PICK = 4;

export function UnitComparePage({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ["project", slug],
    queryFn: () => api<Detail>(`/api/projects/${slug}`, { withAuth: false }),
  });

  // Read initial selection from URL query (?units=id1,id2,id3)
  const initialIds = useMemo(() => {
    if (typeof window === "undefined") return new Set<string>();
    const sp = new URLSearchParams(window.location.search);
    const raw = sp.get("units") ?? "";
    return new Set(
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }, []);

  const [picked, setPicked] = useState<Set<string>>(initialIds);

  useDocumentTitle(
    q.data ? `Összehasonlítás · ${q.data.project.name}` : "Összehasonlítás",
  );

  // Sync picked → URL so a copyable link recreates the selection
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (picked.size === 0) {
      sp.delete("units");
    } else {
      sp.set("units", Array.from(picked).join(","));
    }
    const next = sp.toString() ? `?${sp.toString()}` : "";
    if (next !== window.location.search) {
      window.history.replaceState({}, "", `${window.location.pathname}${next}`);
    }
  }, [picked]);

  if (q.isLoading) {
    return (
      <div className="container py-24 text-brand-500">Betöltés...</div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="container py-24 text-center">
        <h1 className="section-title text-3xl">A projekt nem található</h1>
      </div>
    );
  }

  const { project, units } = q.data;
  // Projekt-szintű ár-elrejtés (shared/pricing.ts `arePricesHidden`).
  const pricesHidden = arePricesHidden(project.pricingDefaults);
  const sorted = [...units].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label),
  );
  const pickedUnits = sorted.filter((u) => picked.has(u.id));

  function toggle(id: string) {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_PICK) next.add(id);
      return next;
    });
  }

  return (
    <div className="bg-white">
      {/* Header band */}
      <section className="border-b border-brand-100 bg-brand-50/50">
        <div className="container py-8">
          <Link
            href={`/projektek/${project.slug}`}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widish font-semibold text-brand-500 hover:text-accent transition-colors"
          >
            <ArrowLeft size={14} />
            <span>{project.name}</span>
          </Link>
          <div className="mt-4">
            <span className="section-eyebrow">Lakások</span>
            <h1 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase tracking-ultrawide text-brand-700">
              Összehasonlítás{" "}
              <span className="text-accent">side-by-side</span>
            </h1>
            <p className="mt-3 text-sm text-brand-500 max-w-2xl">
              Válasszon ki maximum {MAX_PICK} lakást — a műszaki adatok
              és árak egymás mellett jelennek meg.
            </p>
          </div>
        </div>
      </section>

      {/* Unit picker */}
      <section className="py-12 lg:py-16 bg-brand-50">
        <div className="container">
          <ScrollReveal>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <h2 className="text-sm font-semibold uppercase tracking-widish text-brand-500">
                Válasszon lakásokat ({picked.size} / {MAX_PICK})
              </h2>
              {picked.size > 0 && (
                <button
                  type="button"
                  onClick={() => setPicked(new Set())}
                  className="text-xs text-brand-500 hover:text-accent uppercase tracking-widish font-semibold"
                >
                  Kiválasztás törlése
                </button>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {sorted.map((u) => {
                const selected = picked.has(u.id);
                const disabled = !selected && picked.size >= MAX_PICK;
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => toggle(u.id)}
                    disabled={disabled}
                    className={`relative text-left p-4 border-2 transition-all ${
                      selected
                        ? "border-accent bg-white shadow-sm"
                        : disabled
                          ? "border-brand-100 bg-white/50 opacity-50 cursor-not-allowed"
                          : "border-brand-200 bg-white hover:border-brand-400"
                    }`}
                  >
                    {selected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-accent text-white grid place-items-center">
                        <Check size={14} />
                      </div>
                    )}
                    <div className="font-heading text-lg uppercase tracking-widish text-brand-700">
                      {u.label}
                    </div>
                    <div className="mt-1 text-xs text-brand-500">
                      {u.rooms} szoba ·{" "}
                      {formatArea(u.areaSqm as unknown as number | null)}
                    </div>
                    <div className="mt-2 font-medium text-brand-700">
                      {pricesHidden ? (
                        <span className="text-accent font-semibold">
                          Kérjen ajánlatot
                        </span>
                      ) : (
                        formatPriceHuf(unitDisplayPriceHuf(u))
                      )}
                    </div>
                    <div
                      className={`mt-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-widish font-semibold ${UNIT_STATUS_BADGE[u.status]}`}
                    >
                      {UNIT_STATUS_LABEL[u.status]}
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Comparison table */}
      {pickedUnits.length > 0 && (
        <section className="py-12 lg:py-16">
          <div className="container">
            <ScrollReveal>
              <h2 className="text-sm font-semibold uppercase tracking-widish text-brand-500 mb-6">
                Összehasonlítás
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse min-w-[600px]">
                  <thead>
                    <tr>
                      <th className="text-left px-4 py-4 sticky left-0 bg-white z-10 w-[110px] min-w-[110px] border-b border-brand-200" />
                      {pickedUnits.map((u) => (
                        <th
                          key={u.id}
                          className="text-left px-4 py-4 border-b border-brand-200 min-w-[200px] align-top"
                        >
                          <UnitColumnHeader
                            unit={u}
                            projectSlug={project.slug}
                            pricesHidden={pricesHidden}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <CompareRow label="Szobaszám" units={pickedUnits} renderer={(u) => u.rooms ?? "—"} />
                    <CompareRow label="Alapterület" units={pickedUnits} renderer={(u) => formatArea(u.areaSqm as unknown as number | null)} />
                    <CompareRow label="Emelet" units={pickedUnits} renderer={(u) => (u.floor != null ? `${u.floor}.` : "—")} />
                    <CompareRow label="Tájolás" units={pickedUnits} renderer={(u) => u.orientation ?? "—"} />
                    <CompareRow label="Erkély" units={pickedUnits} renderer={(u) => formatArea(u.balconyAreaSqm as unknown as number | null)} />
                    <CompareRow label="Terasz" units={pickedUnits} renderer={(u) => formatArea(u.terraceAreaSqm as unknown as number | null)} />
                    <CompareRow label="Saját kert" units={pickedUnits} renderer={(u) => formatArea(u.gardenAreaSqm as unknown as number | null)} />
                    <CompareRow label="Energiabesorolás" units={pickedUnits} renderer={(u) => u.energyRating ?? "—"} />
                    <CompareRow label="Fűtés" units={pickedUnits} renderer={(u) => u.heatingType ?? "—"} />
                    <CompareRow
                      label="Parkoló"
                      units={pickedUnits}
                      renderer={(u) =>
                        u.parkingIncluded
                          ? u.parkingCount
                            ? `${u.parkingCount} db`
                            : "Igen"
                          : "—"
                      }
                    />
                    <CompareRow
                      label="Tároló"
                      units={pickedUnits}
                      renderer={(u) =>
                        u.storageIncluded
                          ? u.storageAreaSqm
                            ? formatArea(u.storageAreaSqm as unknown as number)
                            : "Igen"
                          : "—"
                      }
                    />
                    <CompareRow
                      label="Státusz"
                      units={pickedUnits}
                      renderer={(u) => UNIT_STATUS_LABEL[u.status]}
                    />
                  </tbody>
                </table>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <Link
                  href={`/projektek/${project.slug}`}
                  className="btn-secondary"
                >
                  <ArrowLeft size={14} />
                  Vissza a projekthez
                </Link>
                <Link
                  href={`/projektek/${project.slug}/muszaki-adatok`}
                  className="btn-secondary"
                >
                  Részletes műszaki adatok
                  <ArrowRight size={14} />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {pickedUnits.length === 0 && (
        <section className="py-20 text-center">
          <p className="text-brand-500">
            Válasszon ki legalább egy lakást a kiválasztó panelből.
          </p>
        </section>
      )}
    </div>
  );
}

function UnitColumnHeader({
  unit,
  projectSlug,
  pricesHidden,
}: {
  unit: ProjectUnit;
  projectSlug: string;
  pricesHidden: boolean;
}) {
  const cover = unit.gallery?.[0];
  return (
    <>
      {cover && (
        <div className="aspect-[4/3] overflow-hidden bg-brand-100 mb-3 max-w-[180px]">
          <img
            src={fileUrl(cover.key)}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <Link
        href={`/projektek/${projectSlug}/${unitSlug(unit)}`}
        className="group inline-flex items-center gap-2"
      >
        <span className="font-heading text-lg uppercase tracking-widish text-brand-700 group-hover:text-accent transition-colors">
          {unit.label}
        </span>
      </Link>
      <div className="mt-1 text-brand-700 font-medium">
        {pricesHidden ? (
          <RequestQuoteLink href={unitQuoteHref(projectSlug, unit)} />
        ) : (
          formatPriceHuf(unitDisplayPriceHuf(unit))
        )}
      </div>
    </>
  );
}

function CompareRow({
  label,
  units,
  renderer,
}: {
  label: string;
  units: ProjectUnit[];
  renderer: (u: ProjectUnit) => string;
}) {
  return (
    <tr>
      <th
        scope="row"
        className="text-left px-4 py-3 text-xs uppercase tracking-widish font-semibold text-brand-500 sticky left-0 bg-white z-10 border-b border-brand-100"
      >
        {label}
      </th>
      {units.map((u) => (
        <td
          key={u.id}
          className="px-4 py-3 text-brand-700 border-b border-brand-100"
        >
          {renderer(u)}
        </td>
      ))}
    </tr>
  );
}
